import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Icons } from '@/src/modules/components/Icons';
import { MarketingHeroReveal, MarketingReveal } from '@/src/modules/components/marketing/MarketingReveal';

interface Feature {
  icon: React.ReactNode;
  name: string;
  description: string;
  upcoming?: boolean;
}

const invitationFeatures: Feature[] = [
  { icon: <Icons.FileText size={24} />, name: 'Digital Invitations', description: 'Create stunning invitations with customizable templates' },
  { icon: <Icons.CheckCircle size={24} />, name: 'RSVP Tracking', description: 'Track acceptances and manage guest responses in real-time' },
  { icon: <Icons.Users size={24} />, name: 'Guest Management', description: 'Organize and manage your guest lists effortlessly' },
  { icon: <Icons.Calendar size={24} />, name: 'Event Scheduling', description: 'Set dates and automate reminders for your events' },
];

const interactiveFeatures: Feature[] = [
  { icon: <Icons.Wedding size={24} />, name: 'Blessings Wall', description: 'Collect beautiful messages and blessings from guests' },
  { icon: <Icons.Gift size={24} />, name: 'Memory Capsule', description: 'Preserve precious memories from your celebration' },
  { icon: <Icons.Camera size={24} />, name: 'Media Gallery', description: 'Share photos and memories with your guests' },
  { icon: <Icons.Chat size={24} />, name: 'Guest Messages', description: 'Enable guest interactions and engagement' },
];

const sharingFeatures: Feature[] = [
  { icon: <Icons.WhatsApp size={24} />, name: 'WhatsApp Sharing', description: 'Share invitations directly via WhatsApp' },
  // TODO(owner): Confirm which features are live before removing Coming Soon badge (QR Sharing, Blessings Wall, Memory Capsule, Watermark Branding).
  { icon: <Icons.Sparkles size={24} />, name: 'QR Sharing', description: 'Generate scannable QR codes for instant access', upcoming: true },
  { icon: <Icons.Link size={24} />, name: 'Smart Links', description: 'Create trackable and shareable invitation links' },
  { icon: <Icons.Corporate size={24} />, name: 'Partner Profiles', description: 'Showcase partner and vendor branding' },
  { icon: <Icons.Analytics size={24} />, name: 'Analytics', description: 'View detailed invitation performance metrics' },
  { icon: <Icons.Palette size={24} />, name: 'Watermark Branding', description: 'Add custom branding to invitations' },
];

function FeatureCard({ feature }: { feature: Feature }) {
  return (
    <motion.div
      whileHover={{ y: -4 }}
      className="group p-6 rounded-2xl bg-surface-low border border-outline-variant/30 hover:border-primary/40 transition-all"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
          {feature.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-bold text-on-surface">{feature.name}</h3>
            {feature.upcoming && (
              <span className="text-[10px] font-bold uppercase bg-status-processing/20 text-status-processing px-2 py-0.5 rounded-full">
                Coming Soon
              </span>
            )}
          </div>
          <p className="text-sm text-on-surface-variant">{feature.description}</p>
        </div>
      </div>
    </motion.div>
  );
}

export function Features() {
  return (
    <div className="flex flex-col w-full bg-surface-lowest">
      {/* Hero */}
      <section className="relative pt-16 pb-12 lg:pt-24 lg:pb-16 overflow-hidden">
        <div className="absolute top-0 right-0 -mr-[15%] -mt-[10%] w-[50%] h-[70%] rounded-full bg-primary/15 blur-[100px] pointer-events-none" />

        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
          <MarketingHeroReveal>
            <h1 className="text-5xl lg:text-6xl font-bold text-on-surface mb-6 leading-tight">
              Powerful features for <span className="text-primary">every celebration</span>
            </h1>
            <p className="text-lg text-on-surface-variant max-w-2xl mx-auto">
              Everything you need to create, share, and manage invitations with confidence. From digital delivery to guest engagement.
            </p>
          </MarketingHeroReveal>
        </div>
      </section>

      {/* Invitation Features */}
      <section className="py-14 border-b border-outline-variant/20">
        <div className="max-w-6xl mx-auto px-6">
          <MarketingReveal className="mb-10">
            <h2 className="text-3xl font-bold text-on-surface mb-2">Invitation Features</h2>
            <p className="text-on-surface-variant">Create and manage invitations like never before</p>
          </MarketingReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {invitationFeatures.map((feature, idx) => (
              <MarketingReveal key={feature.name} delay={idx * 0.04}>
                <FeatureCard feature={feature} />
              </MarketingReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Features */}
      <section className="py-14 border-b border-outline-variant/20">
        <div className="max-w-6xl mx-auto px-6">
          <MarketingReveal className="mb-10">
            <h2 className="text-3xl font-bold text-on-surface mb-2">Interactive Features</h2>
            <p className="text-on-surface-variant">Engage your guests with interactive elements</p>
          </MarketingReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {interactiveFeatures.map((feature, idx) => (
              <MarketingReveal key={feature.name} delay={idx * 0.04}>
                <FeatureCard feature={feature} />
              </MarketingReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Sharing & Partner Features */}
      <section className="py-14 border-b border-outline-variant/20">
        <div className="max-w-6xl mx-auto px-6">
          <MarketingReveal className="mb-10">
            <h2 className="text-3xl font-bold text-on-surface mb-2">Sharing & Partner Features</h2>
            <p className="text-on-surface-variant">Multi-channel sharing and professional branding</p>
          </MarketingReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sharingFeatures.map((feature, idx) => (
              <MarketingReveal key={feature.name} delay={idx * 0.04}>
                <FeatureCard feature={feature} />
              </MarketingReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-20 bg-surface-low border-b border-outline-variant/20 text-center">
        <div className="max-w-4xl mx-auto px-6">
          <MarketingReveal>
            <h2 className="text-3xl lg:text-4xl font-bold text-on-surface mb-4">
              Ready to create your invitation?
            </h2>
            <p className="text-lg text-on-surface-variant max-w-xl mx-auto mb-8">
              Explore our handcrafted templates and personalize every detail in minutes.
            </p>
            <Link
              to="/catalog"
              className="inline-flex items-center justify-center px-8 py-3.5 rounded-xl bg-primary text-white font-semibold text-base shadow-sm hover:bg-primary-dark transition-colors"
            >
              Browse designs
            </Link>
          </MarketingReveal>
        </div>
      </section>
    </div>
  );
}
